import { Credit, CreditStatus, Doctype, DocumentStatus, SurveyQuestion } from '@types';
import { CreditReportGuarantor, CreditReportUser } from '@pages/credit/CreditReport';
import cls from './CreditReportWidget.module.css';
import { useEffect, useMemo, useState } from 'react';
import { CreditFileStatusTypes, creditHttp, surveyHttp, userHttp } from '@network';

export const CreditReportWidget = ({ credit }: { credit: Credit }) => {
  const [requiredDoctypeIds, setRequiredDoctypeIds] = useState<number[]>([]);
  const [profileMissingFields, setProfileMissingFields] = useState<string[]>([]);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [isProfileProgressCompleted, setIsProfileProgressCompleted] = useState<boolean>(false);
  const [guarantorDoctypes, setGuarantorDoctypes] = useState<Doctype[]>([]);
  const [isGuarantorDocumentsCompleted, setGuarantorDocumentsCompleted] = useState<boolean>(false);
  const [isSurveyCompleted, setIsSurveyCompleted] = useState<boolean>(false);
  const isReportsCompleted = true;


  const uploadedDocumentsIds = useMemo(() => {
    return (credit.documents || [])
      .filter(d => (!!d.files?.length && d.status === DocumentStatus.DOCS_READY)
        || !!d.number || d.isOptionalChecked)
      .map(d => d.doctypeId) || [];
  }, [credit.documents]);
  const isClientValid = requiredDoctypeIds
    .every(id => uploadedDocumentsIds.filter(Boolean).includes(id));

  const hasClientGuarantor = !credit.isGuarantorsRequired
    || (credit.guarantors.length > 0 && isGuarantorDocumentsCompleted);


  useEffect(() => {
    credit && (async () => {
      const { surveyQuestions } = await surveyHttp.getCreditSurvey(credit.id);
      setQuestions(surveyQuestions.map((q, index) => {
        q.position = index + 1;
        return q;
      }).filter(q => q.isSendToCredit));
    })();
    credit && (async () => {
      const res = await creditHttp.getCreditDoctypes(credit.id);
      const requiredDoctypeIdsList: number[] = [];
      (res.doctypes || []).forEach(d => {
        if (d.isSendToCredit) {
          requiredDoctypeIdsList.push(d.id);
        }
      });
      setRequiredDoctypeIds(requiredDoctypeIdsList);
      setGuarantorDoctypes(res.guarantorDoctypes);
    })();
  }, [credit]);

  useEffect(() => {
    if (questions.length === 0) {
      setIsSurveyCompleted(true);

      return;
    }

    const totalQuestions = questions
      .filter(question => question.isVisible)
      .reduce((total: Record<'questions' | 'answers', number>, question: SurveyQuestion) => {
        const { cols: answersCols } = question.rows[0] || {};
        const questionsLength = question.answers?.length || 0;
        const answersLength = answersCols?.filter((i: any) => !!i.customAnswer).length || 0;

        return {
          questions: total.questions + questionsLength,
          answers: total.answers + answersLength,
        };
      }, {
        questions: 0,
        answers: 0,
      });

    if (totalQuestions.questions === totalQuestions.answers && totalQuestions.answers > 0) {
      setIsSurveyCompleted(true);
    }
  }, [questions]);

  useEffect(() => {
    if (credit) {
      setGuarantorDocumentsCompleted(!!credit.isGuarantorsRequired);
    }
  }, [credit?.isGuarantorsRequired]);

  useEffect(() => {
    (async () => {
      const res = await userHttp.getProfileProgress(credit.user.id);
      const { profileProgress, missingFields } = res;
      setIsProfileProgressCompleted(profileProgress === 'complete');
      setProfileMissingFields(missingFields);
    })();
  }, []);

  const isFileScreeningDocsReady = [
    CreditStatus.POST_SCREENING,
    CreditStatus.COMPLETED_FILE,
    CreditStatus.BANK_REVISION,
    CreditStatus.APPROVED,
    CreditStatus.IN_NEGOTIATIONS,
    CreditStatus.CONTRACT_IN_PROCESS,
    CreditStatus.SIGNED,
    CreditStatus.REPAID,
    CreditStatus.RESTRUCTURE,
    CreditStatus.DELAY,
  ].includes(credit.status);

  return (
    <div className={cls.wrap}>
      <div className={cls.row}>
        <div className={cls.col}>
          <CreditReportUser
            isClientDocsValid={isClientValid}
            isClientProgressValid={isProfileProgressCompleted}
            hasClientGuarantor={hasClientGuarantor}
            isSurveyCompleted={isSurveyCompleted}
            isReportsCompleted={isReportsCompleted}
            isGuarantorsRequired={!credit.isGuarantorsRequired}
            profileMissingFields={profileMissingFields}
            isFileScreeningDocsReady={isFileScreeningDocsReady}
          />
        </div>
        <div className={cls.col}>
          <CreditReportGuarantor
            guarantor={credit.guarantors[0]}
            guarantorDoctypes={guarantorDoctypes?.filter(d => d.isSendToCredit) || []}
            setGuarantorDocumentsCompleted={setGuarantorDocumentsCompleted}
            isNotRequired={!credit.isGuarantorsRequired}
          />
        </div>
      </div>
    </div>
  );
};
